import './types/global.d'
import 'iframe-resizer/js/iframeResizer.contentWindow'
import './index.css'

import { StyleInjectionProvider } from '@epilot/concorde-elements'
import { lazy, StrictMode, Suspense } from 'react'
import ReactDOM from 'react-dom'
import { I18nextProvider } from 'react-i18next'

import { SpinnerPage } from './components/SpinnerPage'
import { captureSsoCallback } from './blocks-renderers/services/sso-service/callback-capture'
import { captureSsoResume } from './blocks-renderers/services/sso-service/embed-start-capture'
import { setGlobalPackages } from './globalPackages'
import { instance18n } from './locales/i18n'
import { bootstrapOffline } from './offline/bootstrap'
import { ordersLanguage } from './offline/ordersUrl'
import type { BootView } from './offline/types'
import { ordersPageOrUnavailable } from './offline/ui/OrdersUnavailable'
import { initDatadog } from './services/datadog'
import { createShadowFormElement } from './shadow-form/shadow-form'
import {
  TIME_TO_FIRST_RENDER_METRIC,
  TIME_TO_READINESS,
  VERSION
} from './utils/config'
import { isThirdPartyCookiesEnabled } from './utils/cookies'
import { debug } from './utils/debug'
import { setOwnsDocument } from './utils/embed'
import { env } from './utils/env'
import { startTimer } from './utils/timers'
import { TRACE_KEYS, generateTraceId } from './utils/trace'

// Served from our own index.html, so this document is ours — iframe or not.
setOwnsDocument(true)

// The builder's preview styles its scrollbar thin and without a rail
if (new URLSearchParams(window.location.search).get('preview') === 'true') {
  document.documentElement.dataset.preview = 'true'
}

/** Must run before the URL is parsed for app params: OIDC response params would otherwise be
 * swallowed as journey context values. Also puts back the pre-redirect URL, journeyId included. */
captureSsoCallback()

/** Embed-started SSO, standalone leg: captured at boot so it can't stall behind
 * the app's context-validation gates; loads lazily only when a hop was captured. */
if (captureSsoResume()) {
  void import('./blocks-renderers/services/sso-service/embed-start')
    .then(({ runCapturedSsoResume }) => runCapturedSsoResume())
    .catch((originalError: unknown) => {
      /** A deploy can rotate this chunk away mid-session; the journey must
       * still boot (sans resume) instead of dying on an unhandled rejection. */
      const error = new Error('SSO resume could not run')

      error.cause = originalError

      // eslint-disable-next-line no-console
      console.error(error)
    })
}

// Expose some internal packages as global variables for other apps
setGlobalPackages()

debug('Journey App started', {
  STAGE: env('REACT_APP_STAGE'),
  VERSION
})

/**
 * WARNING: This should be always called and should happen before anything else,
 * thus making sure we can trace all sessions, including those that fail early.
 *
 * NOTE: This is supposed to be disabled if third-party tracking is not enabled,
 * but also for the test/local environments.
 */
initDatadog({
  disabled:
    !isThirdPartyCookiesEnabled() ||
    ['test', 'local'].includes(env('REACT_APP_STAGE') ?? 'unknown'),
  mode: 'index'
})

/**
 * Generates an unique trace id for this App's session.
 *
 * NOTE: This should be called before anything else, otherwise we might loose the ability
 * to trace the session to underlying errors/resources.
 */
generateTraceId(TRACE_KEYS.JOURNEY_SESSION_ID)

/**
 * Marks the beginning of times :)
 *
 * We wan't this metric to include the time spent in all operations that preceed
 * the rendering of the first step, thus we should start it as soon as possible.
 */
startTimer(
  TIME_TO_FIRST_RENDER_METRIC,
  'Time spent by the Journey App up until the first render, \
    which is when the user is able to see the actual content of the Journey.'
)

startTimer(
  TIME_TO_READINESS,
  'Time spent by the Journey App up until the App became ready to render the Journey.'
)

const AppShell = lazy(() => import('./AppShell'))

/**
 * Phase 2: the device-wide Orders page, a lazy chunk of the offline add-on (no App, no store). A
 * chunk that cannot load shows why instead of a blank page.
 */
const OrdersPage = lazy(
  ordersPageOrUnavailable(() => import('./offline/ui/OrdersPage'))
)

// TODO: reconsider how soon the AnalyticsProvider can be initialized

const render = (boot: BootView) => {
  // The Orders page has no journey to take a language from.
  if (boot.view === 'orders') {
    void instance18n.changeLanguage(ordersLanguage(navigator.language))
  }

  ReactDOM.render(
    <StyleInjectionProvider>
      <StrictMode>
        <I18nextProvider i18n={instance18n}>
          <Suspense fallback={<SpinnerPage />}>
            {boot.view === 'orders' ? (
              <OrdersPage reason={boot.reason} />
            ) : (
              <AppShell />
            )}
          </Suspense>
        </I18nextProvider>
      </StrictMode>
    </StyleInjectionProvider>,
    document.getElementById('root')
  )

  if (boot.view === 'journey') createShadowFormElement()
}

/**
 * Offline add-on gate. Resolves immediately for every journey not prepared on
 * this device; for a prepared one it loads the add-on first so the config
 * request can be answered from the pack while offline. It also decides when
 * the Orders page renders instead of the app (Phase 2).
 */
void bootstrapOffline().then(render, () => render({ view: 'journey' }))
